import { Geo } from '@getvim/atomic-ui';
import { trim } from 'lodash-es';
import { Address } from '../types';
import { capitalizeAsName } from './general';
import { orderAssistLogger } from '../logger';

export default function addressFormat(address: Partial<Address>): string {
  if (!address) {
    return '';
  }

  const stringAddress = [
    ...[address.address1, address.address2, address.city].map(
      (text) => text && capitalizeAsName(text),
    ),
    address.state,
  ]
    .filter(Boolean)
    .join(', ');
  return [stringAddress, address.zipCode].filter(Boolean).join(' ');
}

const formatters: Record<
  number,
  (
    addressObj: string[],
    zipCode?: string,
    coordinates?: { latitude: number; longitude: number },
  ) => Address
> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  1: (_, zipCode, coordinates) => ({
    zipCode,
    coordinates,
  }),
  // eslint-disable-next-line @typescript-eslint/naming-convention
  2: (addressObj, zipCode, coordinates) => {
    const stateAndZip = addressObj?.[0];
    const hasZipInState = zipCode && stateAndZip?.includes(zipCode);
    const state = hasZipInState ? stateAndZip?.split(' ')[0] : stateAndZip; // might need to change this to state codes

    return {
      state,
      zipCode,
      coordinates,
    };
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  3: (addressObj, zipCode, coordinates) => {
    const stateAndZip = addressObj?.[1];
    const hasZipInState = zipCode && stateAndZip?.includes(zipCode);
    const state = hasZipInState ? stateAndZip?.split(' ')[0] : stateAndZip;

    return {
      city: addressObj?.[0],
      state,
      zipCode,
      coordinates,
    };
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  4: (addressObj, zipCode, coordinates) => {
    const stateAndZip = addressObj?.[2];
    const hasZipInState = zipCode && stateAndZip?.includes(zipCode);
    const state = hasZipInState ? stateAndZip?.split(' ')[0] : stateAndZip;

    return {
      address1: addressObj?.[0],
      city: addressObj?.[1],
      state,
      zipCode,
      coordinates,
    };
  },
};

export const formatToGeo = (address?: Address): Geo | undefined => {
  if (!address) return undefined;
  return {
    address: addressFormat(address),
    zip: address.zipCode,
    // Attach coords only when the address already has a zip (display-complete) so
    // getGeoAddressAndZip short-circuits for a stable "City, ST zip" display. When the
    // zip is missing we omit geocode so LocationSelect geocodes and fetches it.
    ...(address.zipCode && address.coordinates ? { geocode: address.coordinates } : {}),
  };
};

export const formatGeoToAddress = (geo?: Geo | null): Address | undefined => {
  if (!geo?.address) return undefined;

  try {
    const addressObj = geo.address?.split(',').map((item) => trim(item));
    const zipCode = geo.zip;
    const address = formatters[addressObj.length](addressObj, zipCode, geo?.geocode);

    return address;
  } catch (error) {
    orderAssistLogger.warning('Error occurred on formatGeoToAddress', { geo, error });
    return undefined;
  }
};
